import axiosInstance from './config/api';
import {
    WorkflowData,
    PromptsResponse,
    NumberLimitsSettings,
    QueueCadenceSettings,
    SmsPacingSettings,
    TestGenericQualificationRequest,
    TestGenericQualificationResponse,
} from 'pages/WorkflowBuilder/WorkflowBuilder.types';
export const createAgenticWorkflow = (data: WorkflowData) => {
    return axiosInstance.post<{ id: string }>('/workflows', data).then((res) => res.data);
};

export const getEditAgenticWorkflow = (id: string) => {
    return axiosInstance.get<WorkflowData>(`/workflows/edit/${id}`).then((res) => res.data);
};

export const deleteAgenticWorkflow = (id: string) => {
    return axiosInstance.delete<{ message: string }>(`/workflows/${id}`).then((res) => res.data);
};

export const updateAgenticWorkflow = (id: string, data: WorkflowData) => {
    return axiosInstance.put<{ id: string }>(`/workflows/${id}`, data).then((res) => res.data);
};

export type HoldAudioMode = 'ringtone' | 'custom';

export type TransferExperience = {
    holdAudio?: HoldAudioMode;
    holdAudioKey?: string;
};

export type EffectiveTransferExperience = {
    holdAudio: HoldAudioMode;
    holdAudioKey?: string;
    // 'tenant' is reserved for the planned tenant-default tier, so the type
    // does not break when the backend starts returning it.
    source: 'workflow' | 'tenant' | 'none';
};

/** Partial update: keys left out keep whatever the workflow already stores. */
export type WorkflowConfigUpdate = {
    crmCreateContactOnInbound?: boolean;
    queueCadence?: QueueCadenceSettings;
    /** null clears the stored value, so the workflow falls back to the default pacing. */
    smsPacing?: SmsPacingSettings | null;
    /** Super admin only. null clears back to the defaults; the object replaces the stored one. */
    numberLimits?: NumberLimitsSettings | null;
    dispositionLabels?: Record<string, string>;
    /** null clears the stored value; omitting the key leaves it alone. */
    transferExperience?: TransferExperience | null;
    /** Party statuses are create-only, but transitions can be replaced on a saved workflow. */
    statusTransitions?: StatusTransition[];
    /** Prompt row id for inbound calls. null clears it back to Auto (first call node's prompt). */
    inboundPromptId?: string | null;
};

export const updateWorkflowConfig = (id: string, config: WorkflowConfigUpdate) => {
    return axiosInstance.put<WorkflowConfigResponse>(`/workflows/${id}/config`, config).then((res) => res.data);
};

export type LeadFieldDefinition = {
    key: string;
    label?: string;
    required?: boolean;
    example?: string;
    description?: string;
};

/** One configurable party status. Terminal statuses stop further outreach. */
export type PartyStatusDefinition = {
    name: string;
    terminal?: boolean;
    crmIntegrationName?: string;
    pushToCrm?: boolean;
};

/** One condition row of a status transition. */
export type TransitionRule = {
    variable: 'disposition' | 'outbound_status';
    condition: 'last_contains' | 'last_not_contains' | 'history_contains' | 'history_not_contains';
    value: string;
};

/**
 * One transition: when its rules hold, the party moves to target.
 * Evaluated in array order at end-of-node post-processing; first match wins.
 */
export type StatusTransition = {
    rules: TransitionRule[];
    /** Joins the rules. Omitted means 'and'. */
    logic?: 'and' | 'or';
    target: string;
};

/**
 * The prompt inbound calls actually get. `workflow` is an explicit
 * config.inboundPromptId, `first_call_node` is the nearest CALL node with a
 * prompt reached from the head node, `none` means inbound has no prompt.
 */
export type EffectiveInboundPrompt = {
    promptId?: string;
    promptName?: string;
    langfuseName?: string;
    source: 'workflow' | 'first_call_node' | 'none';
};

export type WorkflowConfigResponse = {
    config: {
        leadFields?: LeadFieldDefinition[];
        partyStatuses?: PartyStatusDefinition[];
        statusTransitions?: StatusTransition[];
        crmEnabled?: boolean;
        dispositionLabels?: Record<string, string>;
        transferExperience?: TransferExperience;
        inboundPromptId?: string;
        [k: string]: unknown;
    } | null;
    effectiveOperatingHours?: unknown;
    effectiveTransferExperience?: EffectiveTransferExperience;
    effectiveInboundPrompt?: EffectiveInboundPrompt;
    /** Set when the save went through but a side effect (dispatch rule resync) failed. */
    warning?: string;
};

export const getWorkflowConfig = (id: string) => {
    return axiosInstance.get<WorkflowConfigResponse>(`/workflows/${id}/config`).then((res) => res.data);
};

/** Empty arrays mean this tenant's statuses come from dedicated code. */
export type DefaultStatusConfigResponse = {
    partyStatuses: PartyStatusDefinition[];
    statusTransitions: StatusTransition[];
};

export const getDefaultStatusConfig = () => {
    return axiosInstance.get<DefaultStatusConfigResponse>('/workflows/default-status-config').then((res) => res.data);
};

export const uploadTransferAudio = (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosInstance
        .post<WorkflowConfigResponse>(`/workflows/${id}/transfer-audio`, formData, {
            // The instance defaults to application/json; unsetting it lets the
            // browser write multipart/form-data with its own boundary.
            headers: { 'Content-Type': undefined },
            // A rejected clip is reported inline next to the file input, so the
            // interceptor's toast would say the same thing twice in two wordings.
            suppressErrorToast: true,
        })
        .then((res) => res.data);
};

export const deleteTransferAudio = (id: string) => {
    return axiosInstance
        .delete<WorkflowConfigResponse>(`/workflows/${id}/transfer-audio`, { suppressErrorToast: true })
        .then((res) => res.data);
};

export const getTransferAudio = (id: string) => {
    return axiosInstance
        .get<ArrayBuffer>(`/workflows/${id}/transfer-audio`, {
            responseType: 'arraybuffer',
            suppressErrorToast: true,
        })
        .then((res) => new Blob([res.data], { type: res.headers['content-type'] || 'audio/mpeg' }));
};

export const updateWorkflowStatus = (id: string) => {
    return axiosInstance.put(`/workflows/status/${id}`).then((res) => res.data);
};

export const syncWorkflowPostProcessingConfig = (id: string) => {
    return axiosInstance.put(`/workflows/${id}/post-processing-config`).then((res) => res.data);
};

export const getWorkflowTranscript = (callId: string) => {
    return axiosInstance.get(`/workflows/transcripts/${callId}`).then((res) => res.data);
};

export const getWorkflowAudio = (callId: string) => {
    return axiosInstance
        .get(`/workflows/audio/${callId}`, {
            responseType: 'arraybuffer',
        })
        .then((res) => res.data)
        .then((data) => new Blob([data], { type: 'audio/ogg' }));
};

export const getPrompts = () => {
    return axiosInstance.get<PromptsResponse>('/workflows/node-configs').then((res) => res.data);
};

export const deleteCalls = (callIds: string[]) => {
    return axiosInstance.delete('/workflows/calls', { data: { callIds } }).then((res) => res.data);
};

export const deleteSMS = (smsIds: string[]) => {
    return axiosInstance.delete('/sms', { data: { smsIds } }).then((res) => res.data);
};

export const postTestGenericQualification = ({ transcript, config }: TestGenericQualificationRequest) => {
    return axiosInstance
        .post<TestGenericQualificationResponse>('/workflows/test/generic-qualification', { transcript, config })
        .then((res) => res.data);
};
